import { lazy } from "react";

/**
 * The side panel (chat, source viewer) is its own large chunk with ~40 smaller ones behind it,
 * fetched the first time the panel opens. Over a 120 ms connection that left the panel's space
 * blank for ~2 s in a production build (7 s on the dev server). Surfaces where a chat is likely
 * (the study guide) warm it while the reader is idle or when the learner shows intent (the
 * selection toolbar, the chat dock), so the click shows the panel at once.
 */
type SidePanelModule = typeof import("./SidePanelWithTabsComponent");

let pendingSidePanelModule: Promise<SidePanelModule> | null = null;
let loadedSidePanelComponent: SidePanelModule["default"] | null = null;

/** Starts (or joins) loading the panel's code; a failed load can be retried by the next call. */
export function loadSidePanel(): Promise<SidePanelModule> {
  pendingSidePanelModule ??= import("./SidePanelWithTabsComponent").then(
    (sidePanelModule) => {
      loadedSidePanelComponent = sidePanelModule.default;
      return sidePanelModule;
    },
    (error) => {
      pendingSidePanelModule = null;
      throw error;
    }
  );
  return pendingSidePanelModule;
}

export const LazySidePanelWithTabsComponent = lazy(loadSidePanel);

/** The panel's component when its code is already here, so it can render without a loading frame. */
export function loadedSidePanel(): SidePanelModule["default"] | null {
  return loadedSidePanelComponent;
}

/** Loads the panel when the browser is idle (not on a data-saver connection). Returns a cancel. */
export function prefetchSidePanelWhenIdle(): () => void {
  if (loadedSidePanelComponent || pendingSidePanelModule) return () => undefined;
  const connection = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection;
  if (connection?.saveData) return () => undefined;
  const start = () => { void loadSidePanel().catch(() => undefined); };
  if (typeof window.requestIdleCallback === "function") {
    const handle = window.requestIdleCallback(start, { timeout: 4000 });
    return () => window.cancelIdleCallback(handle);
  }
  const handle = window.setTimeout(start, 1500);
  return () => window.clearTimeout(handle);
}
